import { Vector3 } from 'three';

import { EPSILON } from 'shared/constants';

import type { DimensionDisplayMode } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionSettings';

export interface Vec2 {
    x: number;
    y: number;
}

export interface Rect {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

/**
 * Orthonormal view basis of a wall POI. Looking at the wall along -n:
 * - u points to the viewer's right
 * - v points up (in the wall plane)
 * - n points towards the viewer
 * View coordinates are relative to `origin`.
 */
export interface ViewFrame {
    origin: Vector3;
    u: Vector3;
    v: Vector3;
    n: Vector3;
}

/** Maps view coordinates into display coordinates: x' = x, y' = offsetY + scaleY * y */
export interface DisplayMapping {
    mode: DimensionDisplayMode;
    scaleY: number;
    offsetY: number;
    /** depth (along n) of the drawing plane relative to the frame origin */
    depth: number;
}

export const PROJECTION_VERTICAL_SCALE = 0.4;
const PROJECTION_FLOOR_GAP = 0.3;
const OVERLAY_DEPTH_GAP = 0.01;

export function createViewFrame(normal: Vector3, origin: Vector3): ViewFrame {
    const n = normal.clone().normalize();
    const u = new Vector3(-n.y, n.x, 0);
    if (u.lengthSq() < EPSILON) {
        u.set(1, 0, 0);
    }
    u.normalize();
    const v = new Vector3().crossVectors(n, u).normalize();
    return { origin: origin.clone(), u, v, n };
}

const tmp = new Vector3();

export function toView(frame: ViewFrame, p: { x: number; y: number; z: number }): Vec2 {
    tmp.set(p.x - frame.origin.x, p.y - frame.origin.y, p.z - frame.origin.z);
    return { x: tmp.dot(frame.u), y: tmp.dot(frame.v) };
}

export function toDepth(frame: ViewFrame, p: { x: number; y: number; z: number }): number {
    tmp.set(p.x - frame.origin.x, p.y - frame.origin.y, p.z - frame.origin.z);
    return tmp.dot(frame.n);
}

/**
 * - projection: mirrored and compressed drawing below the floor (folded down around the floor line)
 * - overlay: 1:1 front view placed directly in front of the formwork
 */
export function createDisplayMapping(
    mode: DimensionDisplayMode,
    floorY: number,
    frontDepth: number,
    verticalScale = PROJECTION_VERTICAL_SCALE
): DisplayMapping {
    if (!Number.isFinite(verticalScale) || verticalScale < 0.1 || verticalScale > 1) {
        throw new RangeError('Dimension vertical scale must be between 0.1 and 1.');
    }
    if (mode === 'overlay') {
        return { mode, scaleY: 1, offsetY: 0, depth: frontDepth + OVERLAY_DEPTH_GAP };
    }

    const foldY = floorY - PROJECTION_FLOOR_GAP;
    return {
        mode,
        scaleY: -verticalScale,
        offsetY: foldY * (1 + verticalScale),
        depth: 0
    };
}

export function mapPoint(mapping: DisplayMapping, p: Vec2): Vec2 {
    return { x: p.x, y: mapping.offsetY + mapping.scaleY * p.y };
}

export function mapRect(mapping: DisplayMapping, r: Rect): Rect {
    const y1 = mapping.offsetY + mapping.scaleY * r.minY;
    const y2 = mapping.offsetY + mapping.scaleY * r.maxY;
    return { minX: r.minX, maxX: r.maxX, minY: Math.min(y1, y2), maxY: Math.max(y1, y2) };
}

/** Converts a display-space length along y back into the real length. */
export function unmapLengthY(mapping: DisplayMapping, displayLength: number): number {
    return Math.abs(displayLength / mapping.scaleY);
}

export function emptyRect(): Rect {
    return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

export function extendRect(r: Rect, p: Vec2): Rect {
    if (p.x < r.minX) {
        r.minX = p.x;
    }
    if (p.y < r.minY) {
        r.minY = p.y;
    }
    if (p.x > r.maxX) {
        r.maxX = p.x;
    }
    if (p.y > r.maxY) {
        r.maxY = p.y;
    }
    return r;
}

export function rectOfPoints(points: Vec2[]): Rect {
    const r = emptyRect();
    for (const p of points) {
        extendRect(r, p);
    }
    return r;
}

export function isRectValid(r: Rect): boolean {
    return r.minX <= r.maxX && r.minY <= r.maxY;
}

export function unionRect(a: Rect, b: Rect): Rect {
    return {
        minX: Math.min(a.minX, b.minX),
        minY: Math.min(a.minY, b.minY),
        maxX: Math.max(a.maxX, b.maxX),
        maxY: Math.max(a.maxY, b.maxY)
    };
}
